import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Valeur finale du "wipe" des médias : un peu plus grande que la boîte,
// pour ne pas couper les ombres, les halos ni le zoom au survol.
const REVEAL_END = 'inset(-10% -10% -10% -10% round 20px)';

/**
 * Scrollytelling des pages.
 * À placer sur le conteneur de la page : .projectdemo (fiches projet) ou .aboutme.
 * Chaque effet s'active seulement si les éléments existent dans la page.
 */
export default class Scrollytelling {
  constructor(element) {
    this.element = element;
    this.q = gsap.utils.selector(this.element);
    this.refresh = () => ScrollTrigger.refresh();

    this.options = {
      // Paragraphes dont les <strong> se soulignent au fil du scroll
      markSelector:
        '.projectintro p, .difficultes p, .step .text p, .who p, .softskill p, .contactintro p',
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.documentElement.classList.add('has-scrollytelling');

    this.ctx = gsap.context(() => {
      this.initHero();
      this.initHeadings();
      this.initMarks();
      this.initSteps();
      this.initAbout();
    }, this.element);

    // Les images et les polices changent la hauteur de la page : on remesure
    window.addEventListener('load', this.refresh, { once: true });
    document.fonts?.ready.then(this.refresh);
  }

  /* ---------- Hero des fiches projet ---------- */

  initHero() {
    const hero = this.element.querySelector('.projecthero');
    if (!hero) return;

    // Les infos arrivent une à une au chargement
    gsap.from(hero.querySelectorAll('.projectinfobox .left > *, .projectinfobox .right > *'), {
      y: 30,
      opacity: 0,
      duration: 0.9,
      ease: 'power3.out',
      stagger: 0.08,
      delay: 0.2,
      clearProps: 'transform,opacity', // rend la main au CSS (survol des boutons)
    });

    // L'image zoome et s'efface doucement quand on quitte le hero
    const img = hero.querySelector('.projectmedia img');
    if (img) {
      gsap.to(img, {
        scale: 1.06,
        opacity: 0.55,
        ease: 'none',
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
      });
    }
  }

  /* ---------- Titres de section ---------- */

  initHeadings() {
    this.q('section h1').forEach((h1) => {
      gsap.from(h1, {
        y: 40,
        opacity: 0,
        duration: 0.9,
        ease: 'power3.out',
        scrollTrigger: { trigger: h1, start: 'top 88%', toggleActions: 'play none none reverse' },
      });
    });
  }

  /* ---------- Mots-clés : le soulignement se dessine ---------- */

  initMarks() {
    this.q(this.options.markSelector).forEach((p) => {
      const marks = [...p.querySelectorAll('strong')];
      if (!marks.length) return;

      marks.forEach((m) => m.classList.add('scrolly-mark'));

      // --mark va de 0 à 1 (voir scrollytelling.scss), un mot-clé après l'autre
      gsap.to(marks, {
        '--mark': 1,
        ease: 'none',
        stagger: 0.4,
        scrollTrigger: { trigger: p, start: 'top 85%', end: 'top 40%', scrub: 0.5 },
      });
    });
  }

  /* ---------- Étapes du processus ---------- */

  initSteps() {
    this.q('.projectsteps .step').forEach((step) => {
      const text = step.querySelectorAll('.text h2, .text p');
      const visual = step.querySelector('.media, .imageswiper');

      // Texte et média se révèlent ensemble, liés au scroll
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: step,
          start: 'top 85%',
          end: 'top 40%',
          scrub: 0.6,
          invalidateOnRefresh: true, // recalcule le sens du wipe si la mise en page change
        },
      });

      tl.from(text, { y: 40, opacity: 0, ease: 'none', stagger: 0.2, duration: 0.6 }, 0);

      if (visual) {
        tl.fromTo(
          visual,
          { clipPath: () => this.clipFrom(step), opacity: 0, y: 40 },
          { clipPath: REVEAL_END, opacity: 1, y: 0, ease: 'power2.out', duration: 1 },
          0.15
        );
      }

      // L'étape au centre de l'écran est à pleine lumière (classe is-current)
      ScrollTrigger.create({
        trigger: step,
        start: 'top 60%',
        end: 'bottom 40%',
        toggleClass: { targets: step, className: 'is-current' },
      });
    });
  }

  // Le média "sort" depuis le côté du texte (ou de haut en bas en une colonne)
  clipFrom(step) {
    const columns = getComputedStyle(step).gridTemplateColumns.trim().split(/\s+/).length;
    if (columns < 2) return 'inset(0% 0% 100% 0% round 20px)';
    return step.classList.contains('reversed')
      ? 'inset(0% 0% 0% 100% round 20px)'
      : 'inset(0% 100% 0% 0% round 20px)';
  }

  /* ---------- Page À propos ---------- */

  initAbout() {
    const about = this.element.querySelector('.aboutinfo');
    if (!about) return;

    // Les trois colonnes arrivent l'une après l'autre
    // (y et opacity seulement : le TiltEffect de la photo utilise déjà les autres transformations)
    gsap.from(about.querySelectorAll(':scope > *'), {
      y: 60,
      opacity: 0,
      duration: 1,
      ease: 'power3.out',
      stagger: 0.15,
      scrollTrigger: { trigger: about, start: 'top 80%' },
    });

    gsap.from(about.querySelectorAll('.passions li'), {
      x: -24,
      opacity: 0,
      duration: 0.7,
      ease: 'power3.out',
      stagger: 0.12,
      clearProps: 'transform,opacity',
      scrollTrigger: { trigger: about.querySelector('.passions'), start: 'top 75%' },
    });

    const softList = this.element.querySelector('.softskilllist');
    if (softList) {
      gsap.from(softList.children, {
        y: 50,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.12,
        clearProps: 'transform,opacity',
        scrollTrigger: { trigger: softList, start: 'top 82%' },
      });
    }

    const hardList = this.element.querySelector('.hardskilllist');
    if (hardList) {
      gsap.from(hardList.children, {
        scale: 0.6,
        opacity: 0,
        duration: 0.6,
        ease: 'back.out(1.7)',
        stagger: 0.06,
        clearProps: 'transform,opacity',
        scrollTrigger: { trigger: hardList, start: 'top 85%' },
      });
    }

    // La vidéo grandit à mesure qu'elle entre à l'écran
    const video = this.element.querySelector('.videopresentation .video');
    if (video) {
      gsap.fromTo(
        video,
        { scale: 0.88, opacity: 0.4 },
        {
          scale: 1,
          opacity: 1,
          ease: 'none',
          scrollTrigger: { trigger: video, start: 'top 92%', end: 'top 45%', scrub: true },
        }
      );
    }

    const methods = this.element.querySelector('.contactmethods');
    if (methods) {
      gsap.from(methods.children, {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.15,
        clearProps: 'transform,opacity',
        scrollTrigger: { trigger: methods, start: 'top 85%' },
      });
    }
  }

  destroy() {
    this.ctx?.revert();
    window.removeEventListener('load', this.refresh);
    document.documentElement.classList.remove('has-scrollytelling');
  }
}
